// Persistent left sidebar — the single home for brand, key stats, filters,
// and the events list. Replaces the previous scatter of floating pills.
//
// Mobile (<sm): the body collapses behind a hamburger toggle so the map
// stays visible. The header strip remains always-on. Expanded mobile state
// fills the available viewport width minus inset.
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FilterState } from '../lib/events';
import { aggregateStats } from '../lib/events';
import { emptyFilters } from '../lib/url-state';
import { FilterDrawer } from './filters';
import { EventList } from './event-list';
import { startTour } from './onboarding-tour';
import { config } from '../lib/config';
import type { EventRecord } from '../lib/events';

interface Props {
  events: EventRecord[];
  visible: EventRecord[];
  filters: FilterState;
  onFilters: (next: FilterState) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export function Sidebar({
  events, visible, filters, onFilters, selectedId, onSelect,
}: Props) {
  const { t } = useTranslation();
  // Stats reflect the currently-visible (filtered) slice — events / cases /
  // deaths recompute as the user adjusts timeline, category, secondary, or
  // confidence filters. When no filter is dirty, these collapse to the
  // lifetime totals.
  const visibleStats = useMemo(() => aggregateStats(visible), [visible]);
  const activeCount = useMemo(
    () => events.filter(o => o.status === 'active').length,
    [events],
  );
  const dataAsOf = useMemo(() => latestIngestion(events), [events]);
  const isFiltered = visible.length !== events.length;

  // Mobile: body collapsed by default so the map dominates. Desktop ignores
  // this state and always renders the body via sm: breakpoint.
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <aside
      className="pointer-events-auto flex w-[calc(100vw-32px)] max-w-[400px] flex-col overflow-hidden rounded-2xl border border-white/60 bg-white/96 backdrop-blur-xl sm:w-[320px]"
      style={{
        maxHeight: 'calc(100vh - 32px)',
        boxShadow:
          '0 0 0 1px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.9) inset, 0 24px 48px -8px rgba(0,0,0,0.32), 0 8px 16px -4px rgba(0,0,0,0.18)',
      }}
    >
      {/* ============================================================
       * Header — wordmark + active/freshness + mobile toggle
       * ============================================================ */}
      <header data-tour="brand" className="flex items-center justify-between border-b border-[var(--color-line)] px-4 py-3">
        <a href="/" className="group inline-flex items-center gap-2">
          <Logo />
          <span className="text-[14px] font-semibold tracking-tight text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
            {config.app.domain}
          </span>
        </a>
        <div className="flex items-center gap-2">
          {activeCount > 0 ? (
            <button
              data-tour="active-badge"
              type="button"
              onClick={() => onFilters({
                ...emptyFilters(),
                statuses: new Set(['active']),
              })}
              title="Show active events (clears the timeline filter)"
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-signal-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-signal)] ring-1 ring-[var(--color-signal)]/15 hover:bg-[var(--color-signal)]/15 hover:ring-[var(--color-signal)]/30"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="signal-pulse absolute inline-flex h-full w-full rounded-full bg-[var(--color-signal)] opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--color-signal)]" />
              </span>
              <span className="tabular-nums">{activeCount}</span>
              <span>{t('sidebar.active_badge', { count: activeCount }).replace(/^[\d\s]+/, '').trim() || 'active'}</span>
            </button>
          ) : dataAsOf ? (
            <span className="mono text-[10.5px] text-[var(--color-text-3)]">
              {dataAsOf}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? t('sidebar.collapse_panel') : t('sidebar.expand_panel')}
            aria-expanded={mobileOpen}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-3)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)] sm:hidden"
          >
            {mobileOpen
              ? <ChevronUp size={14} strokeWidth={2.25} />
              : <ChevronDown size={14} strokeWidth={2.25} />}
          </button>
        </div>
      </header>

      <div className={(mobileOpen ? 'flex' : 'hidden') + ' flex-col overflow-hidden sm:flex'}>

      {/* ============================================================
       * Stats — three big numbers
       * ============================================================ */}
      <section data-tour="stats" className="grid grid-cols-3 gap-px border-b border-[var(--color-line)] bg-[var(--color-line)]">
        <Stat
          value={visibleStats.events.toLocaleString()}
          label={t('sidebar.events_label')}
          tone={isFiltered ? 'accent' : undefined}
        />
        <Stat
          value={formatBig(visibleStats.counts)}
          label={t('sidebar.cases_label')}
          tone={isFiltered ? 'accent' : undefined}
        />
        <Stat
          value={(visibleStats.byField.deaths ?? 0).toLocaleString()}
          label={t('sidebar.deaths_label')}
          tone={(visibleStats.byField.deaths ?? 0) > 0 ? 'signal' : undefined}
        />
      </section>
      {isFiltered && (
        <div className="flex items-center justify-between border-b border-[var(--color-line)] bg-[var(--color-accent-soft)]/60 px-4 py-1 text-[10.5px] font-medium uppercase tracking-wide text-[var(--color-accent)]">
          <span>
            {t('sidebar.filtered_summary', { visible: visible.length, total: events.length })}
          </span>
        </div>
      )}

      {/* ============================================================
       * Filters — compact inline row
       * ============================================================ */}
      <section data-tour="filters" className="border-b border-[var(--color-line)] px-4 py-2.5">
        <FilterDrawer
          filters={filters}
          onChange={onFilters}
          visibleCount={visible.length}
          totalCount={events.length}
          inline
        />
      </section>

      {/* ============================================================
       * Events list — internal scroll keeps the card compact
       * ============================================================ */}
      <section data-tour="events-list" className="min-h-0 flex-1 overflow-y-auto border-b border-[var(--color-line)] py-2.5">
        <EventList
          events={visible}
          selectedId={selectedId}
          onSelect={onSelect}
          inline
        />
      </section>

      {/* ============================================================
       * Footer
       * ============================================================ */}
      <footer className="flex flex-col gap-2 border-t border-[var(--color-line)] px-3 py-3 text-[12px]">
        <div className="grid grid-cols-2 gap-1.5">
          <NavButton href="/history" label={t('nav.history')} />
          <NavButton href="/about" label={t('nav.about')} />
          <NavButton href="/faq" label={t('nav.faq')} />
          <NavButton
            label={t('sidebar.tour_link')}
            icon={<HelpCircle size={13} strokeWidth={2.25} />}
            onClick={() => startTour()}
            title={t('sidebar.tour_link')}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <span className="mono text-[10px] text-[var(--color-text-4)]">CC-BY-4.0</span>
        </div>
      </footer>
      </div>
    </aside>
  );
}

/** Small fixed-height button used in the sidebar footer nav grid. Acts
 *  as a link when `href` is provided, a button otherwise. The grid
 *  layout in the footer keeps every cell the same width regardless of
 *  the label length. */
function NavButton({
  href, label, icon, onClick, title,
}: {
  href?: string;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  title?: string;
}) {
  const className =
    'inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-line)] bg-white px-3 text-[12.5px] font-medium text-[var(--color-text-2)] transition-colors hover:border-[var(--color-line-strong)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-accent)]';
  if (href) {
    return (
      <a href={href} title={title} className={className}>
        {icon}
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} title={title} className={className}>
      {icon}
      {label}
    </button>
  );
}

function Stat({
  value, label, tone,
}: { value: string; label: string; tone?: 'signal' | 'accent' }) {
  const valueColor =
    tone === 'signal'
      ? 'text-[var(--color-signal)]'
      : tone === 'accent'
      ? 'text-[var(--color-accent)]'
      : 'text-[var(--color-text)]';
  return (
    <div className="bg-white px-3 py-3">
      <div className={`text-[18px] font-semibold leading-tight tabular-nums ${valueColor}`}>
        {value}
      </div>
      <div className="mt-0.5 text-[10.5px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
        {label}
      </div>
    </div>
  );
}


/** App brand mark — matches /favicon.svg, /apple-touch-icon.svg,
 *  /logo.svg, /og-default.svg. Apps replace these assets via app.config.ts.branding.
 *  See frontend/public/branding/ for the SVG sources. */
function Logo() {
  return (
    <svg
      viewBox="0 0 64 64"
      className="h-6 w-6 shrink-0"
      aria-hidden
      focusable="false"
    >
      <defs>
        <radialGradient id="hm-env-sb" cx="38%" cy="32%" r="70%">
          <stop offset="0%" stopColor="#aaa3f3" />
          <stop offset="55%" stopColor="#5247e5" />
          <stop offset="100%" stopColor="#312e81" />
        </radialGradient>
        <g id="hm-spike-sb">
          <rect x="-1" y="-21.5" width="2" height="3.2" fill="#5247e5" />
          <rect x="-2.2" y="-25" width="4.4" height="4.4" rx="0.6" fill="#5247e5" />
          <rect x="-1.8" y="-24.6" width="1.4" height="1.2" rx="0.3" fill="#fafaf9" fillOpacity="0.32" />
        </g>
      </defs>
      <g transform="translate(32 32)">
        <use href="#hm-spike-sb" />
        <use href="#hm-spike-sb" transform="rotate(30)" />
        <use href="#hm-spike-sb" transform="rotate(60)" />
        <use href="#hm-spike-sb" transform="rotate(90)" />
        <use href="#hm-spike-sb" transform="rotate(120)" />
        <use href="#hm-spike-sb" transform="rotate(150)" />
        <use href="#hm-spike-sb" transform="rotate(180)" />
        <use href="#hm-spike-sb" transform="rotate(210)" />
        <use href="#hm-spike-sb" transform="rotate(240)" />
        <use href="#hm-spike-sb" transform="rotate(270)" />
        <use href="#hm-spike-sb" transform="rotate(300)" />
        <use href="#hm-spike-sb" transform="rotate(330)" />
      </g>
      <circle cx="32" cy="32" r="19" fill="url(#hm-env-sb)" />
      <circle cx="32" cy="32" r="19" fill="none" stroke="#312e81" strokeOpacity="0.55" strokeWidth="0.8" />
      <circle cx="32" cy="32" r="16" fill="none" stroke="#fafaf9" strokeOpacity="0.18" strokeWidth="0.7" />
      <g fill="none" stroke="#fafaf9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 24.5 27 q 4 -3 8 0 q 4 3 -1 5 q -5 1 -7 -5" strokeOpacity="0.62" strokeWidth="1.5" />
        <path d="M 33 36 q 3.5 -2.2 6 0 q 1.8 2.4 -2 3.4 q -3.6 0.5 -4 -3.4" strokeOpacity="0.55" strokeWidth="1.3" />
        <path d="M 27 38 q 2.4 -1.6 4 0 q 1.2 1.6 -1 2.2 q -2.4 0.4 -3 -2.2" strokeOpacity="0.5" strokeWidth="1.2" />
      </g>
    </svg>
  );
}

function latestIngestion(rows: EventRecord[]): string | null {
  let max = 0;
  for (const r of rows) {
    const t = Date.parse(r.ingestion.last_updated);
    if (!Number.isNaN(t) && t > max) max = t;
  }
  if (!max) return null;
  return new Date(max).toISOString().slice(0, 10);
}

function formatBig(n: number): string {
  if (n >= 100_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return n.toLocaleString();
}
